import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { PortfolioProject } from "@/hooks/usePortfolioProjects";
import { calculateROI, calculateBCR, calculatePaybackMonths } from "@/data/roiModelData";
import { LIDER_IA_COMPONENTS, DIMENSION_NAMES } from "@/data/liderIAEvaluationData";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChartContainer, ChartConfig } from "@/components/ui/chart";
import { formatCurrency as fmtCurrency } from "@/lib/formatCurrency";

const COLORS = ["hsl(var(--primary))", "hsl(var(--success))", "hsl(var(--warning))"];

function getProjectROI(p: PortfolioProject) {
  if (!p.roiData || !p.roiData.costs || !p.roiData.benefits) {
    return { costs: 0, benefits: 0, roi: 0, bcr: 0, payback: 0 };
  }
  const c = p.roiData.costs;
  const costs = (c.subscriptions || 0) + (c.tokenConsumption || 0) + (c.personnel || 0) + (c.training || 0);
  const benefits = Array.isArray(p.roiData.benefits)
    ? p.roiData.benefits.reduce((s, b) => s + (b.type === "not_defined" ? 0 : b.amount), 0)
    : 0;
  return {
    costs,
    benefits,
    roi: calculateROI(benefits, costs),
    bcr: calculateBCR(benefits, costs),
    payback: calculatePaybackMonths(benefits, costs),
  };
}

const makeFormatCurrency = (currency: string) => (v: number) => fmtCurrency(v, currency);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: PortfolioProject[];
  currency?: string;
}

export function ProjectComparisonDialog({ open, onOpenChange, projects, currency = "COP" }: Props) {
  const formatCurrency = makeFormatCurrency(currency);
  if (projects.length < 2) return null;

  // Dimension bars data
  const dimensions = (["L", "I", "D", "E", "R"] as const);
  const dimensionData = dimensions.map(dim => {
    const row: Record<string, any> = { dimension: DIMENSION_NAMES[dim] };
    projects.forEach((p, i) => {
      // Use totalScore proportionally as we don't have per-dimension scores in portfolio
      row[`p${i}`] = p.totalScore;
    });
    return row;
  });

  const chartConfig: ChartConfig = {};
  projects.forEach((p, i) => {
    chartConfig[`p${i}`] = { label: p.name.slice(0, 20), color: COLORS[i] };
  });

  const rois = projects.map(getProjectROI);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Comparación de Proyectos</DialogTitle>
          <DialogDescription>Comparando {projects.length} proyectos seleccionados</DialogDescription>
        </DialogHeader>

        {/* Score comparison */}
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${projects.length}, 1fr)` }}>
          {projects.map((p, i) => (
            <div key={p.id} className="p-4 rounded-xl border border-border/50 bg-muted/30 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                <h4 className="font-semibold text-foreground text-sm truncate">{p.name}</h4>
              </div>
              <p className="text-xs text-muted-foreground">{p.company || "Sin empresa"}</p>
              <div className="flex items-center gap-2 mt-2">
                <Progress value={(p.totalScore / 500) * 100} className="flex-1 h-2" />
                <span className="font-bold text-foreground text-lg">{p.totalScore}</span>
              </div>
              <Badge variant="outline" className="text-xs">{p.area || "Sin área"}</Badge>
            </div>
          ))}
        </div>

        {/* ROI comparison table */}
        <div className="mt-4">
          <h3 className="font-semibold text-foreground mb-3 text-sm">Comparación de ROI</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 text-muted-foreground font-medium">Métrica</th>
                  {projects.map((p, i) => (
                    <th key={p.id} className="text-right py-2 font-medium" style={{ color: COLORS[i] }}>
                      {p.name.slice(0, 18)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">Score LIDER-IA</td>
                  {projects.map(p => <td key={p.id} className="text-right py-2 font-bold">{p.totalScore}</td>)}
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">Costos</td>
                  {rois.map((r, i) => <td key={i} className="text-right py-2 text-destructive">{formatCurrency(r.costs)}</td>)}
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">Beneficios</td>
                  {rois.map((r, i) => <td key={i} className="text-right py-2 text-success">{formatCurrency(r.benefits)}</td>)}
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">ROI %</td>
                  {rois.map((r, i) => <td key={i} className="text-right py-2 font-bold">{Math.round(r.roi)}%</td>)}
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">BCR</td>
                  {rois.map((r, i) => <td key={i} className="text-right py-2">{r.bcr.toFixed(2)}x</td>)}
                </tr>
                <tr>
                  <td className="py-2 text-muted-foreground">Payback</td>
                  {rois.map((r, i) => (
                    <td key={i} className="text-right py-2">
                      {r.payback === Infinity ? "∞" : `${r.payback.toFixed(1)} meses`}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Tools comparison */}
        <div className="mt-4">
          <h3 className="font-semibold text-foreground mb-3 text-sm">Herramientas</h3>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${projects.length}, 1fr)` }}>
            {projects.map((p, i) => (
              <div key={p.id} className="flex flex-wrap gap-1">
                {p.tools.length > 0 ? p.tools.map(t => (
                  <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                )) : <span className="text-xs text-muted-foreground">Sin herramientas</span>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
